import React from "react";

const formatLabel = (text) => {
    if (!text) return "";
    const str = String(text).trim();
    if (!str) return "";

    const words = str.split(" ");
    if (words.length === 1) {
        return [str];
    }

    const totalLength = str.length;
    const target = totalLength / 2;
    let current = "";
    let splitIndex = words.length;

    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const test = current ? `${current} ${word}` : word;
        const testLength = test.length;

        if (testLength >= target && current) {
            splitIndex = i;
            break;
        }

        current = test;
    }

    const firstLine = words.slice(0, splitIndex).join(" ");
    const secondLine = words.slice(splitIndex).join(" ");

    return secondLine ? [firstLine, secondLine] : [firstLine];
};

const toNumberOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;
    const num = Number(str);
    return Number.isNaN(num) ? null : num;
};

const SERIES_CONFIG = {
    nle: { label: "NLE", color: "#8f66b4" },
    pml: { label: "PML", color: "#ef8500" },
    mfl: { label: "MFL", color: "#f2c53d" },
};

const getNiceStep = (rawStep) => {
    if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const normalized = rawStep / magnitude;
    if (normalized <= 1) return 1 * magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
};

const buildAxisScale = (maxValue, tickCount = 6) => {
    const safeMax = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 0;
    const rawStep = safeMax / Math.max(1, tickCount - 1);
    const step = getNiceStep(rawStep || 1);
    const axisMax = Math.max(step, Math.ceil(safeMax / step) * step);
    const ticks = [];
    for (let value = 0; value <= axisMax; value += step) {
        ticks.push(value);
    }
    if (ticks[ticks.length - 1] !== axisMax) {
        ticks.push(axisMax);
    }
    return { axisMax, ticks };
};

export default function LossScenarioChart({ rows = [] }) {
    const [visibleSeries, setVisibleSeries] = React.useState({
        nle: true,
        pml: true,
        mfl: true,
    });

    const items = (rows || [])
        .map((row) => {
            const risk = (row.risk || "").trim();
            if (!risk) return null;

            const nleVal = toNumberOrNull(row.nle);
            const pmlVal = toNumberOrNull(row.pml);
            const mflVal = toNumberOrNull(row.mfl);

            // Require at least one of the values to be present
            if (nleVal === null && pmlVal === null && mflVal === null) {
                return null;
            }

            const round2 = (v) => parseFloat((v ?? 0).toFixed(2));

            const nleForGraph = round2(nleVal ?? 0);

            // PML in the chart is (PML_from_loss - NLE_from_loss), floored at 0
            let pmlForGraph = 0;
            if (pmlVal !== null) {
                const diff = pmlVal - nleForGraph;
                pmlForGraph = round2(Math.max(0, diff));
            }

            // MFL in the chart is MFL_from_loss - (NLE_graph + PML_graph)
            let mflForGraph = 0;
            if (mflVal !== null) {
                const mflBase = mflVal;
                const mflDiff = mflBase - (nleForGraph + pmlForGraph);
                mflForGraph = round2(Math.max(0, mflDiff));
            }

            return {
                label: formatLabel(risk),
                nle: nleForGraph,
                pml: pmlForGraph,
                mfl: mflForGraph,
                total: round2(nleForGraph + pmlForGraph + mflForGraph),
            };
        })
        .filter(Boolean)
        .reverse();

    const displayItems = React.useMemo(
        () =>
            items.map((item) => {
                const nle = visibleSeries.nle ? item.nle : 0;
                const pml = visibleSeries.pml ? item.pml : 0;
                const mfl = visibleSeries.mfl ? item.mfl : 0;
                return {
                    ...item,
                    nle,
                    pml,
                    mfl,
                    total: parseFloat((nle + pml + mfl).toFixed(2)),
                };
            }),
        [items, visibleSeries]
    );

    const sortedDisplayItems = React.useMemo(() => {
        return [...displayItems].sort((a, b) => {
            if (b.total !== a.total) return b.total - a.total;
            return a.label.join(" ").localeCompare(b.label.join(" "));
        });
    }, [displayItems]);

    const axisMaxFromData = sortedDisplayItems.reduce((max, item) => Math.max(max, item.total), 0);
    const { axisMax, ticks } = React.useMemo(
        () => buildAxisScale(axisMaxFromData, 6),
        [axisMaxFromData]
    );

    const formatValue = (value) => {
        if (!Number.isFinite(value)) return "0";
        if (Math.abs(value - Math.round(value)) < 0.001) return String(Math.round(value));
        return String(parseFloat(value.toFixed(1)));
    };

    const toggleSeries = (key) => {
        setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <div className="w-full overflow-x-auto">
            <div className="min-w-[900px] rounded bg-white p-6">
                <div className="grid grid-cols-[160px_minmax(0,1fr)_120px] gap-x-6">
                    <div className="space-y-5">
                        {sortedDisplayItems.map((item) => (
                            <div key={item.label.join("-")} className="min-h-[36px] text-right text-xs font-bold uppercase text-gray-900">
                                {item.label.map((line, index) => (
                                    <div key={`${line}-${index}`} className="leading-tight">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div>
                        <div className="space-y-5">
                            {sortedDisplayItems.map((item) => {
                                if (item.total <= 0) {
                                    return <div key={`${item.label.join("-")}-bar`} className="h-9" />;
                                }

                                const totalWidth = axisMax > 0 ? (item.total / axisMax) * 100 : 0;
                                const t = item.total > 0 ? item.total : 1;
                                const { nle, pml, mfl } = item;

                                const pmlPlusNlePct = ((nle + pml) / t) * 100;
                                const pmlBase = nle + pml;
                                const nleInsidePmlPct =
                                    pmlBase > 0 ? (nle / pmlBase) * 100 : 0;
                                const nlePctOfBar = (nle / t) * 100;

                                const outerSeriesKey = mfl > 0 ? "mfl" : pml > 0 ? "pml" : nle > 0 ? "nle" : null;
                                const outerColor = outerSeriesKey ? SERIES_CONFIG[outerSeriesKey].color : "#d1d5db";

                                return (
                                    <div key={`${item.label.join("-")}-bar`} className="relative h-9">
                                        <div
                                            className="relative h-full overflow-hidden rounded-full border-[3px]"
                                            style={{ width: `${totalWidth}%`, borderColor: outerColor, backgroundColor: outerColor }}
                                        >
                                            {/* Nested design: MFL (outer) → PML → NLE (inner). Widths stay correct when any series is hidden. */}
                                            {mfl > 0 && pml > 0 && (
                                                <div
                                                    style={{ width: `${pmlPlusNlePct}%` }}
                                                    className="absolute left-0 top-0 h-full overflow-hidden rounded-full border-[3px] border-[#ef8500] bg-[#ef8500]"
                                                >
                                                    {nle > 0 && (
                                                        <div
                                                            style={{ width: `${nleInsidePmlPct}%` }}
                                                            className="h-full rounded-full border-[3px] border-[#8f66b4] bg-[#8f66b4]"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {mfl > 0 && pml <= 0 && nle > 0 && (
                                                <div
                                                    style={{ width: `${nlePctOfBar}%` }}
                                                    className="absolute left-0 top-0 h-full rounded-full border-[3px] border-[#8f66b4] bg-[#8f66b4]"
                                                />
                                            )}
                                            {mfl <= 0 && pml > 0 && (
                                                <div
                                                    className="absolute left-0 top-0 h-full w-full overflow-hidden rounded-full border-[3px] border-[#ef8500] bg-[#ef8500]"
                                                >
                                                    {nle > 0 && (
                                                        <div
                                                            style={{ width: `${nleInsidePmlPct}%` }}
                                                            className="h-full rounded-full border-[3px] border-[#8f66b4] bg-[#8f66b4]"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {mfl <= 0 && pml <= 0 && nle > 0 && (
                                                <div className="absolute left-0 top-0 h-full w-full rounded-full border-[3px] border-[#8f66b4] bg-[#8f66b4]" />
                                            )}
                                            <span className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-sm font-bold text-gray-900">
                                                {formatValue(item.total)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-7 px-1">
                            <div className="flex items-center justify-between text-lg font-medium text-gray-900">
                                {ticks.map((tick) => (
                                    <span key={tick}>{tick}</span>
                                ))}
                            </div>
                            <div className="mt-1 text-center text-xl text-gray-900">(USDM)</div>
                        </div>
                    </div>

                    <div className="flex items-center">
                        <div className="space-y-6 text-sm font-bold text-gray-700">
                            {(["nle", "pml", "mfl"]).map((seriesKey) => (
                                <button
                                    key={seriesKey}
                                    type="button"
                                    onClick={() => toggleSeries(seriesKey)}
                                    className={`flex items-center gap-3 text-left transition-opacity ${visibleSeries[seriesKey] ? "opacity-100" : "opacity-40"}`}
                                >
                                    <span className="h-5 w-8 rounded-full" style={{ backgroundColor: SERIES_CONFIG[seriesKey].color }} />
                                    <span>{SERIES_CONFIG[seriesKey].label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
